import ArrowRight from '@mui/icons-material/ForwardRounded';
import Container from '@mui/material/Container';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Skeleton from '@mui/material/Skeleton';
import { Breakpoint, styled, Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Process, { ProcessCard } from '@qpr/sipoc/components/Process';
import React from 'react';

const PREFIX = 'ProcessChain';

const classes = {
  root: `${PREFIX}-root`,
  gridList: `${PREFIX}-gridList`,
  arrowContainer: `${PREFIX}-arrowContainer`,
  arrow: `${PREFIX}-arrow`,
  tileContent: `${PREFIX}-tileContent`
};

const Root = styled('div')(() => ({
  [`& .${classes.gridList}`]: {
    '&::-webkit-scrollbar': {
      height: '7px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '3px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555'
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '3px'
    },
    scrollbarColor: '#888 #f1f1f1',
    scrollbarWidth: 'thin'
  },

  [`& .${classes.arrowContainer}`]: {
    float: 'right',
    display: 'table',
    height: '100%'
  },

  [`& .${classes.arrow}`]: {
    display: 'table-cell',
    color: '#BED5DE',
    verticalAlign: 'middle',
    paddingTop: '45px',
    paddingBottom: '45px'
  },

  [`& .${classes.tileContent}`]: {
    height: '100%',
    width: '100%',
    display: 'table'
  }
}));

type BreakpointOrNull = Breakpoint | null;

/**
 * Be careful using this hook. It only works because the number of
 * breakpoints in theme is static. It will break once you change the number of
 * breakpoints. See https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level
 */
function useWidth() {
  const theme: Theme = useTheme();
  const keys: readonly Breakpoint[] = [...theme.breakpoints.keys].reverse();
  return (
    keys.reduce((output: BreakpointOrNull, key: Breakpoint) => {
      const matches = useMediaQuery(theme.breakpoints.up(key));
      return !output && matches ? key : output;
    }, null) || 'xs'
  );
}

type ProcessChainProps = {
  cards: ProcessCard[];
  inited: boolean;
};

const ProcessChain = ({ cards, inited }: ProcessChainProps) => {
  const width = useWidth();

  const getGridListCols = () => {
    if (width === 'xl') {
      return 4;
    }

    if (width === 'lg') {
      return 3;
    }

    if (width === 'md') {
      return 2;
    }

    return 1;
  };

  const getProcessCell = (
    card: ProcessCard,
    idx: number,
    theCards: Array<ProcessCard>
  ): JSX.Element => {
    return (
      <ImageListItem
        sx={{ height: 'auto', flexShrink: 0, width: `${100 / getGridListCols()}%` }}
        key={card.title}
      >
        {inited ? (
          <div className={classes.tileContent} data-testid="sipoc-upper-cell">
            {idx < theCards.length - 1 ? (
              <div className={classes.arrowContainer}>
                <span className={classes.arrow} data-testid="sipoc-upper-arrow">
                  <ArrowRight fontSize="large" />
                </span>
              </div>
            ) : null}
            <Process item={card} key={card.id} />
          </div>
        ) : (
          <Skeleton variant="rectangular" height={132} data-testid="sipoc-upper-skeleton" />
        )}
      </ImageListItem>
    );
  };

  console.log(getGridListCols());
  return (
    <Root>
      <Container
        sx={{ display: 'flex', flexWrap: 'wrap', overflow: 'hidden' }}
        maxWidth="xl"
        component="main"
        data-testid={inited ? 'sipoc-upper-main' : 'sipoc-upper-loading'}
      >
        <ImageList
          className={classes.gridList}
          sx={{ display: 'flex', flexWrap: 'nowrap', overflowY: 'auto', width: '100%' }}
        >
          {cards && cards.map((card, idx) => getProcessCell(card, idx, cards))}
        </ImageList>
      </Container>
    </Root>
  );
};

export default ProcessChain;
